import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Lock } from 'lucide-react'
import { firebaseAuthService } from '../services/firebaseAuthService'
import { useUser } from '../contexts/UserContext'
import { auth } from '../config/firebase'

const SimpleLoginPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  })

  const [error, setError] = useState<string | null>(null)
  const [resetMessage, setResetMessage] = useState<string | null>(null)
  const [resetLoading, setResetLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const { user, isAuthenticated, isLoading, refreshUser } = useUser()

  useEffect(() => {
    if (isLoading || !isAuthenticated || !user) return
    if (user.userType !== 'ADMIN' && !user.emailVerified) {
      navigate('/verify-email', { replace: true })
    } else {
      navigate('/dashboard', { replace: true })
    }
  }, [isLoading, isAuthenticated, user, navigate])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (error) setError(null)
    if (resetMessage) setResetMessage(null)
  }

  const handleForgotPassword = async () => {
    setError(null)
    setResetMessage(null)

    const email = (formData.email || '').trim()
    if (!email) {
      setError('Please enter your email address first')
      return
    }

    setResetLoading(true)
    try {
      const continueUrl = `${window.location.origin}/login`
      const result = await firebaseAuthService.sendPasswordReset(email, continueUrl)
      if (!result.success) {
        setError(result.error || 'Failed to send password reset email')
        return
      }
      // Avoid leaking account existence details
      setResetMessage('If an account exists for that email, a password reset link has been sent. Check spam/promotions — it can take a minute.')
    } catch (err: any) {
      setError(err.message || 'Failed to send password reset email')
    } finally {
      setResetLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.email || !formData.password) {
      setError('Please fill in all fields')
      return
    }

    setLoading(true)

    try {
      const result = await firebaseAuthService.signIn(formData.email, formData.password)

      if (result.success) {
        // Login must never send a verification email (that is signup / manual resend only).
        sessionStorage.removeItem('verificationEmailSent')
        sessionStorage.removeItem('justRegistered')

        const profile = await refreshUser()
        if (!profile) {
          setError('Signed in, but we could not load your account. Please try again in a moment.')
          return
        }
        if (auth.currentUser) {
          await auth.currentUser.reload()
        }
        const needsVerify = profile.userType !== 'ADMIN' && auth.currentUser && !auth.currentUser.emailVerified
        navigate(needsVerify ? '/verify-email' : '/dashboard', { replace: true })
      } else {
        setError(result.error || 'Login failed')
      }
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-neutral-50 via-white to-primary-50/30 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-xl border border-neutral-100 p-8 md:p-10">
          <div>
            <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/20">
              <Lock className="h-7 w-7 text-white" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-neutral-900 tracking-tight">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-neutral-600">
              Welcome back to HarvestHelp
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email Address
              </label>
              <div className="mt-1 relative">
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="block w-full px-3 py-2.5 pl-10 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                  placeholder="Enter your email"
                />
                <Mail className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="block w-full px-3 py-2.5 pl-10 border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
                  placeholder="Enter your password"
                />
                <Lock className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {resetMessage && (
            <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-md text-sm">
              {resetMessage}
            </div>
          )}

          <div className="flex items-center justify-end">
            <button
              type="button"
              onClick={handleForgotPassword}
              disabled={resetLoading}
              className="text-sm font-medium text-primary-600 hover:text-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {resetLoading ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-semibold rounded-2xl text-white bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-400 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </div>

          <div className="text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
                Create one here
              </Link>
            </p>
          </div>
        </form>
        </div>
      </div>
    </div>
  )
}

export default SimpleLoginPage
